/* eslint-disable no-useless-escape */
import cn from 'classnames'
import { TFunction } from 'i18next'
import { CountryCode, isSupportedCountry, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js/max'
import debounce from "lodash/debounce"
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { renderToString } from 'react-dom/server'
import { useTranslation } from 'react-i18next'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'
import { clearTimeout } from 'timers'
import { useMenuEntries } from '../components/Container/Dashboard/DashboardSideBar/items'
import { DashboardMenuItem } from '../components/Container/Dashboard/DashboardSideBar/utils'
import { useDashboardAnalytics } from '../components/Container/Onboarding/useDashboardAnalytics'
import HttpLink from '../components/HttpLink'
import { Phone } from '../components/Presentational/PhoneInput'
import { appConfig, appInit } from '../config'
import { useIBState } from 'ib/utils/hooks'
import { executeApi } from 'ib/utils/ibApi'
import { getQuestions, getUserApps, loadCountries, loadDocTypes, logout, sendLastField } from '../redux/actions'
import { AppState } from '../redux/appState'
import { appError, setAppStatus, setIB, setPageTitle, setUserAppInfo, setUserInsidePortal } from '../redux/slices/app'
import { Store } from '../redux/store'
import api from './api'
import { mobileDevice } from './constants/browser'
import { getIBCountryWhitelist, useIBOnboarding } from './constants/IBs'
import { NON_MINI_TMBM_COUNTRIES } from './constants/NON_MINI_TMBM_COUNTRIES'
import { AccountsSort, AccountTradingTypes, allJapaneseSymbolsRegex, kanjiNameRegex, katakanaNameRegex, KNOWN_ORGS, nameNumbersRegex, nameSpecialSymbolsRegex, PlatformMap, PortalAccountDomain, ScreenSize, TradeAccountType, WTRLanguage, __STORAGE } from './enums'
import { EU_EEA_COUNTRY_CODES, filterToEuEea, isEuRestricted } from './euCountries'
import { useVisualSteps } from './onboarding'
import { useReCaptcha } from './reCAPTCHA'
import routes, { ibPath } from './routes'
import { setScrollTop } from './scroll'
import { useThemes } from './themes'
import { Account, AccountCreationFlow, AccountStat, AccountTradingTypeModel, AccountTradingTypeUnion, AddressFields, AppInfo, ApplicationStatus, Country, DocTypes, DocTypesMap, FieldValidation, IBCookie, IndexedObject, QuestionsIDs, RemainingDeposit, SelectOption, TradingAccountType, TradingPlatform, TradingPlatformFullName, TradingPlatformID, TradingPlatformName, UserApplication, WtrAdditionalParams } from './types'
import { useCurrencyName } from './useCurrencyName'
import { isThinkRewardsAvailable } from './thinkRewards'
import Utils, { EMAIL_MAX_LENGTH } from './utils'

function usePrevious(value: any) {
    const ref = useRef(null)
    useEffect(() => {
        ref.current = value
    }, [value])
    return ref.current
}

const useShortTranslation = (languageString: string) => {
    const { t } = useTranslation()
    const tt = (name: string, options?: Object) => {
        return t(`${languageString}${name}`, options)
    }
    return tt
}

const useScrollToTop = () => {
    useEffect(() => {
        setScrollTop(0)
    }, [])
}

const usePageTitle = (key: string | null) => {
    const dispatch = useDispatch()
    useEffect(() => {
        dispatch(setPageTitle(key))
        return () => { dispatch(setPageTitle(null)) }
    }, [dispatch, key])
}

const useThousandsFormatter = (minimumFractionDigits: number = 2, maximumFractionDigits: number = 8) => {
    const getFormat = useCallback((currency?: string, minDecimals?: number, maxDecimals?: number) =>
    ({
        style: 'currency' as const,
        currency,
        currencyDisplay: 'code' as const,
        minimumFractionDigits: typeof minDecimals === 'number' ? minDecimals : minimumFractionDigits,
        maximumFractionDigits: typeof maxDecimals === 'number' ? maxDecimals : maximumFractionDigits
    }), [minimumFractionDigits, maximumFractionDigits])
    const language = useSelector((state: Store) => state.App.language)

    const thousandsFormatter = useCallback((value: number, currency: string = 'USD', minDecimals: number = 2, maxDecimals: number = 8) => {
        if (currency === 'USDT') currency = 'USD'
        return value.toLocaleString(language, getFormat(currency, minDecimals, maxDecimals))
            .replace(/[a-z]{3}/i, "").trim()
    }, [language, getFormat])

    return thousandsFormatter
}

const useDateTimeFormatter = () => {
    const { portalAccountDomain } = useSelector((state: Store) => state.App)
    const isTMJPaccount = PortalAccountDomain.TMJP === portalAccountDomain

    const dateTimeFormatter = useCallback((value?: string, isTimeIncluded: boolean = false) => {
        const utcDate = moment.utc(value)
        const base = isTMJPaccount ? 'YYYY年MM月DD日' : 'DD/MM/YYYY'
        const formatPattern = isTimeIncluded ? `${base} HH:mm` : base
        return moment(utcDate).local().format(formatPattern)
    }, [isTMJPaccount])

    return dateTimeFormatter
}

const useCurrencyFormatter = (currencyCode?: string) => {
    const thousandsFormatter = useThousandsFormatter()

    const formatCurrency = useCallback((value: number, code?: string) => {
        try {
            const resultCurrency = code || currencyCode
            if (resultCurrency && ['BTC', 'ETH', 'DASH', 'BCH', 'LTC', 'USDT', 'XLM', 'EOS', 'XRP', 'USDC'].includes(resultCurrency.toUpperCase())) {
                return Utils.fixToSpecificDecimal(value || 0, 8)
            }
            if (resultCurrency === 'JPY') {
                return thousandsFormatter(value || 0, resultCurrency, 0, 0)
            }
            return thousandsFormatter(Utils.floorToTwoDecimals(value || 0), resultCurrency)
        } catch (error) {
            return String(value)
        }
    }, [currencyCode, thousandsFormatter])

    return formatCurrency
}

const useFormatAmount = (baseCurrency?: string, isBehind: boolean = true) => {
    const formatter = useCurrencyFormatter()

    const format = useCallback((value: number, code?: string) => {
        const currency = code || baseCurrency

        const valueSpan = <span data-di-mask data-dd-privacy="mask" className="amountClass">{formatter(value, currency)}</span>
        const signSpan = <span>{Utils.getCurrencySign(currency)}</span>
        return isBehind ? (
            <span>
                {valueSpan}
                &nbsp;
                {signSpan}
            </span>
        ) : (
            <span>
                {signSpan}
                &nbsp;
                {valueSpan}
            </span>
        )
    }, [baseCurrency, formatter, isBehind])

    return format
}

const UseLinks = (links: IndexedObject | string) => {
    return (name: string) => {
        if (Utils.isObject(links)) {
            const link = (links as IndexedObject)[name]
            if (link) {
                return <HttpLink url={link.url}>{link.name}</HttpLink>
            }
            else {
                return name
            }
        }
        return name
    }
}


const makeEnsureLoaded = (
    selector: (state: Store) => boolean,
    action: () => { type: string },
) => () => {
    const dispatch = useDispatch()
    const loaded = useSelector(selector)
    useEffect(() => {
        if (!loaded) dispatch(action())
    }, [loaded, dispatch])
}

export const useEnsureDocTypes = makeEnsureLoaded(
    state => state.App.docTypes.length > 0,
    loadDocTypes,
)

export const useEnsureCountries = makeEnsureLoaded(
    state => state.App.countries.length > 0,
    loadCountries,
)

export const useDocTypeTitle = () => {
    const { t } = useTranslation()
    const languageString = 'account:upload'
    const { portalAccountDomain, userProfile } = useAppState()
    const isSouthAfrica = userProfile?.country?.code3 === "ZAF"
    const isThailand = userProfile?.country?.code3 === 'THA'

    const getDocTypeTitle = useCallback((type?: DocTypes): string => {
        if (!portalAccountDomain || !userProfile) return ''
        const domain = KNOWN_ORGS.includes(portalAccountDomain)
            ? portalAccountDomain
            : KNOWN_ORGS.includes(userProfile.organization?.name)
                ? userProfile.organization.name
                : userProfile.country?.organization?.name ?? portalAccountDomain

        let docTitleLangString = isSouthAfrica
            ? `${languageString}.documents.TFSA`
            : `${languageString}.documents.${domain}`
        if (isSouthAfrica) docTitleLangString += '.ZAF'
        if (portalAccountDomain === PortalAccountDomain.TMNZ)
            docTitleLangString = `${languageString}.documents.AU`
        if (portalAccountDomain === PortalAccountDomain.TMLC)
            docTitleLangString = `${languageString}.documents.TMBM`
        switch (type) {
            case 'passport': return t(`${docTitleLangString}.passport`)
            case 'license': {
                if (isThailand) {
                    return t(`${docTitleLangString}.THA.driverLicence`)
                }
                return t(`${docTitleLangString}.driverLicence`)
            }
            case 'id': {
                if (Utils.checkDIFCentity(userProfile?.country, portalAccountDomain))
                    return t(`${docTitleLangString}.uaeNationalID`)
                return t(`${docTitleLangString}.nationalID`)
            }
            case 'address': return t(`${docTitleLangString}.bill`)
            case 'bank': return t(`${docTitleLangString}.bankStatement`)
            case 'card': return t(`${docTitleLangString}.creditCardStatement`)
            case 'other': return t(`${docTitleLangString}.other`)
            case 'photo': return t(`${docTitleLangString}.photo`)
            case 'photoSecondary': return t(`${docTitleLangString}.photoSecondary`)
            case 'incorporationArticles': return t(`${docTitleLangString}.incorporationArticles`)
            case 'addressSecondary': return t(`${docTitleLangString}.addressSecondary`)
            case 'addressCompany': return t(`${docTitleLangString}.addressCompany`)
            case 'addressCorporation': return t(`${docTitleLangString}.addressCorporation`)
            case 'postOfficeBox': return t(`${docTitleLangString}.postOfficeBox`)
            case 'avatar': return t(`${docTitleLangString}.avatar`)
            case 'visa': return t(`${docTitleLangString}.visa`)
            case 'Proof of ID': return t(`${docTitleLangString}.Proof of ID`)
        }
        // fallback to english
        return type ? (DocTypesMap as any)[type].name : ''
    }, [t, portalAccountDomain, isSouthAfrica, isThailand, userProfile])

    return getDocTypeTitle
}

export const useDocTypeDescription = () => {
    const { t } = useTranslation()
    const languageString = 'account:upload.documentsDescription'
    const userProfile = useSelector((state: Store) => state.App.userProfile)
    const isPhilippines = userProfile?.country?.code3 === "PHL"

    const getDocTypeDescription = useCallback((type?: DocTypes): string => {
        if (isPhilippines) {
            let docTitleLangString = `${languageString}.PHL`
            switch (type) {
                case 'id': return t(`${docTitleLangString}.acceptedDocumentTypes`)
            }
        }
        return ''
    }, [t, isPhilippines])

    return getDocTypeDescription
}

const useIsGeneralSidebarEnabled = () => {
    const store = useSelector((state: Store) => state.App)
    const { liveAppCompleted, demoAccounts, appStatus, hasCompletedApp } = store

    const enabled = demoAccounts.length
        ? true
        : hasCompletedApp || liveAppCompleted || ['APPROVED',
            'PENDING_REVIEW',
            'DENIED',
            'FAILED',
            'PENDING_TRANSFER',
            'PENDING_ID_ADDRESS',
            'PENDING_KYC',
            'MANUAL_DOCS',
            'LEVEL1_APPROVED',
            'PENDING_ID',
            'PENDING_ADDRESS',
            'INTERNAL_TESTING',
            'PENDING_NEW_TnC_ACCEPTANCE',
            'PENDING_IP_MISMATCH',
            'PENDING_DOB_MISMATCH',
            'PENDING_BLURRY_POA',
            'PENDING_BLURRY_CROPPED_ID',
            'PENDING_TRANSLATION',
            'PENDING_APPROPRIATENESS_TEST'].includes(appStatus!)
    return enabled
}

const usePlatformName = () => {
    const { t } = useTranslation()
    return useCallback((name?: TradingPlatformName): string => {
        if (!name) return ''
        switch (name) {
            case 'MT4': return 'MetaTrader 4'
            case 'MT5': return 'MetaTrader 5'
            default: return t(`common:platformName.${name}`)
        }
    }, [t])
}

const use2FACheck = () => {
    const history = useHistory()
    const { twoFa: { checked, enabled, valid } } = useAppState()

    const check = useCallback(() => {
        if (checked) {
            if (enabled && !valid) {
                history.push(routes.account.check2fa)
                return false
            }
            return true
        }
        return false
    }, [checked, enabled, history, valid])

    return check
}

const useUserApplications = () => {
    const { uncompletedApplication, userApps, demoAccounts,
        accountsLoaded, loggedIn } = useAppState()
    const { checkForSimplifiedOnboarding } = useSimplifyOnboardingCheck()

    const validApps = useMemo(() => {
        return userApps.every(a => a.accountTradingType)
    }, [userApps])

    const hasNoApplication = useMemo(() => userApps.length === 0, [userApps.length])

    // TFPRODEF-3702: these selectors previously returned false for the WHOLE list
    // when ANY application lacked accountTradingType (validApps gate). A malformed
    // duplicate application (e.g. an incomplete level-one app) then hid the
    // client's approved application from routing, landing them on the wrong
    // profile at login. Guard per application instead of poisoning the list.
    const hasCEAccount = useMemo(() => {
        return userApps.find(x => x.accountTradingType?.accountTradingType === 'CE') !== undefined
    }, [userApps])

    const hasCFDAccount = useMemo(() => {
        return userApps.find(x => x.accountTradingType?.accountTradingType === 'CFD') !== undefined
    }, [userApps])

    const incompleteApplication = useMemo(() => {
        return userApps.find(x => x.applicationStatus === 'INCOMPLETE')
    }, [userApps])

    const levelOneApprovedApp = useMemo(() => {
        return userApps.find(x => x.applicationStatus === 'LEVEL1_APPROVED')
    }, [userApps])

    const approvedApp = useMemo(() => {
        return userApps.find(x => x.applicationStatus === 'APPROVED')
    }, [userApps])

    const simplifiedAppNotApprovedYet = useMemo(() => {
        if (checkForSimplifiedOnboarding()) {
            const hasApproved = userApps.find(x => x.applicationStatus === 'APPROVED')
            if (hasApproved) return false

            const resultApp = userApps.find(x => x.applicationStatus !== 'APPROVED')
            if (resultApp?.platformAccountType === 'Money_Manager') {
                return false
            }
            return !!resultApp
        }
        return false
    }, [checkForSimplifiedOnboarding, userApps])

    const hasSimplifiedAppExpired = useMemo(() => {
        const app = userApps.find(x => x.isSimplifiedOnboarding &&
            x.applicationStatus !== 'APPROVED')
        if (!app) return false
        return moment().diff(moment(app.createdDate), 'days') > 60
    }, [userApps])


    const hasFailed = useMemo(() => {
        return userApps.find(x => [
            'PENDING_APPROPRIATENESS_TEST',
            'PENDING_RG227',
            'DENIED',
            'FAILED'
        ].includes(x.applicationStatus)) !== undefined
    }, [userApps])

    const failedApp = useMemo(() => {
        return userApps.find(x => [
            'DENIED',
            'FAILED'
        ].includes(x.applicationStatus))
    }, [userApps])

    const hasUnexpiredFailed = useMemo(() => {
        return failedApp
            ? moment().diff(moment(failedApp.createdDate), 'days') < 90
            : false
    }, [failedApp])

    // TFPRODEF-3702: approval detection must not depend on unrelated applications
    // being well-formed; see the note above hasCEAccount.
    const hasApproved = useMemo(() => {
        return userApps.find(x => x.applicationStatus === 'APPROVED') !== undefined
    }, [userApps])

    const hasApprovedCFD = useMemo(() => {
        return userApps.find(x => x.accountTradingType?.accountTradingType === 'CFD'
            && x.applicationStatus === 'APPROVED') !== undefined
    }, [userApps])

    const hasPendingReview = useMemo(() => {
        return userApps.find(x => x.applicationStatus === 'PENDING_REVIEW') !== undefined
    }, [userApps])

    const hasThinkCopy = useMemo(() => {
        return userApps.find(x => x.platformAccountType === 'CFD_Copy') !== undefined
    }, [userApps])

    const introducedBy = useMemo(() => {
        return userApps.find(x => x.introducedBy !== undefined)?.introducedBy
    }, [userApps])

    const SBApplication = useMemo(() => {
        return userApps.find(x => x.platformAccountType === 'spreadBetting')
    }, [userApps])

    const hasPAMMManager = useMemo(() => {
        return userApps.find(x => x.platformAccountType === 'Money_Manager') !== undefined
    }, [userApps])

    const hasPAMMInvestor = useMemo(() => {
        return userApps.find(x => x.platformAccountType === 'Investor') !== undefined
    }, [userApps])

    const hasApprovedCE = useMemo(() => {
        return userApps.find(x => x.accountTradingType?.accountTradingType === 'CE'
            && x.applicationStatus === 'APPROVED') !== undefined
    }, [userApps])

    const notApprovedCFD = useMemo(() => {
        return userApps.filter(x => x.accountTradingType?.accountTradingType === 'CFD'
            && !['APPROVED', 'DUPLICATE'].includes(x.applicationStatus))
    }, [userApps])

    const notApprovedCE = useMemo(() => {
        return userApps.filter(x => x.accountTradingType?.accountTradingType === 'CE'
            && !['APPROVED', 'DUPLICATE'].includes(x.applicationStatus))
    }, [userApps])

    const notApprovedApps = useMemo(() => {
        return [...notApprovedCFD, ...notApprovedCE]
    }, [notApprovedCFD, notApprovedCE])

    const hasCFDApps = useMemo(() => {
        return userApps.filter(x => x.accountTradingType?.accountTradingType === 'CFD').length > 0
    }, [userApps])

    const hasCEApps = useMemo(() => {
        return userApps.filter(x => x.accountTradingType?.accountTradingType === 'CE').length > 0
    }, [userApps])

    const latestApplication = useMemo(() => {
        return userApps.slice().sort((x, y) => Utils.sortDates('descend', x.createdDate, y.createdDate))[0]
    }, [userApps])

    const internalApplication = useMemo(() => loggedIn &&
        (hasApproved ||
            (accountsLoaded && demoAccounts.length > 0)),
        [accountsLoaded, demoAccounts.length, hasApproved, loggedIn])

    const hasOngoingApplication = useMemo(() => {
        return !!userApps.find(x => !['APPROVED', 'DENIED', 'FAILED', 'ALREADY_REGISTERED', 'DUPLICATE']
            .includes(x.applicationStatus))
    }, [userApps])

    const pendingDocsApplication = useMemo(() => {
        return userApps.find(x => ['PENDING',
            'PENDING_ID_ADDRESS',
            'PENDING_ID',
            'PENDING_ADDRESS',
            'PENDING_PROPER_DOCUMENTS',
            'PENDING_E_VERIFICATION',
            'PENDING_E_VERIFICATION_RETRY',
            'PENDING_KYC',
            'MANUAL_DOCS']
            .includes(x.applicationStatus))
    }, [userApps])

    const userPlatforms = useMemo<TradingPlatformName[]>(() => {
        return userApps ? userApps.map(a => PlatformMap[a.platformId])
            : []
    }, [userApps])

    // THPRT-6269: an application parked by the BE pending an appropriateness-test retake
    const parkedApplication = useMemo(() => {
        return userApps.find(x => x.applicationStatus === 'PENDING_APPROPRIATENESS_TEST')
    }, [userApps])

    return {
        hasCEAccount,
        hasCFDAccount,
        hasApprovedCFD,
        hasApprovedCE,
        hasCFDApps,
        hasCEApps,
        incompleteApplication,
        levelOneApprovedApp,
        approvedApp,
        simplifiedAppNotApprovedYet,
        hasPendingReview,
        hasFailed,
        latestApplication,
        internalApplication,
        hasApproved,
        hasNoApplication,
        notApprovedCFD,
        notApprovedCE,
        uncompletedApplication,
        hasOngoingApplication,
        userPlatforms,
        notApprovedApps,
        failedApp,
        hasUnexpiredFailed,
        validApps,
        hasPAMMManager,
        hasPAMMInvestor,
        hasThinkCopy,
        hasSimplifiedAppExpired,
        pendingDocsApplication,
        parkedApplication,
        SBApplication,
        introducedBy
    }
}

const useCompleteAppLaterButton = () => {
    const { t } = useTranslation('account')
    const dispatch = useDispatch()
    const history = useHistory()
    const completeLater = useCallback(() => {
        dispatch(setUserInsidePortal(true))
        history.push(routes.dashboard.accounts.root)
    }, [dispatch, history])

    return <button
        className={cn('button', 'black-border')}
        onClick={completeLater}
    >{t('completeLater')}</button>
}

const useGetAccountTypesOptions = (t: TFunction, tradingTypes: AccountTradingTypeModel[]) => {
    // const shouldAllowCE = !!tradingTypes.find(x => x.accountTradingType === 'CE')

    const accountTypes: SelectOption[] = useMemo(() => {
        const available = [{
            name: t('account:type.individual'),
            value: 'individual'
        }, {
            name: t('account:type.corporate'),
            // hint: shouldAllowCE ? t('account:corporateCEwarning') : undefined,
            hint: t('account:corporateCEwarning'),
            hintPlace: 'right' as ReactTooltip.Place,
            value: 'corporate',
            disabled: true
        }]
        return available
    }, [t])

    return accountTypes
}

const useAccounts = () => {
    const store: AppState = useSelector((state: Store) => state.App)
    const { liveAccounts, demoAccounts } = store
    const availabeForDeposit = useMemo(() => Utils.getAccountsForDeposit(liveAccounts), [liveAccounts])
    const availabeForTransfer = useMemo(() => Utils.getAccountsForTransferOrWithdraw(liveAccounts), [liveAccounts])
    const enabledLiveAccounts = useMemo(() => liveAccounts.filter(a => !a.archived &&
        a.platformAccountType !== "Money_Manager")
        , [liveAccounts])
    const totalLiveAccounts = useMemo(() => liveAccounts.filter(a => !a.archived)
        , [liveAccounts])
    const enabledDemoAccounts = useMemo(() => demoAccounts.filter(a => !a.archived), [demoAccounts])
    const allEnabledAccounts = useMemo(() => [...enabledLiveAccounts, ...enabledDemoAccounts], [enabledDemoAccounts, enabledLiveAccounts])
    const allLiveEnabledTTcfds = useMemo(() => enabledLiveAccounts.filter(x => x.account.accountTradingType.accountTradingType === 'CFD'
        && x.account.platform.name === 'ThinkTrader'), [enabledLiveAccounts])
    const hasLiveAccounts = useMemo(() => liveAccounts.length > 0, [liveAccounts])
    const hasDemoAccounts = useMemo(() => demoAccounts.length > 0, [demoAccounts.length])
    const latestLiveAccount = useMemo(() => {
        if (liveAccounts.length === 0) return undefined
        let account = undefined
        let id = 0
        liveAccounts.forEach(x => {
            if (x.account.id > id) {
                id = x.account.id
                account = x
            }
        })
        return account || liveAccounts[0]
    }, [liveAccounts])
    const isDemo = useMemo(() => hasDemoAccounts && !hasLiveAccounts, [hasDemoAccounts, hasLiveAccounts])
    const hasFundedLiveAccount = useMemo(() => !!liveAccounts.find(x => !x.archived && x.balance > 0), [liveAccounts])
    const TTcfdsWithBalance = useMemo(() => allLiveEnabledTTcfds.filter(account => account.balance > 0), [allLiveEnabledTTcfds])
    const hasThinkCopy = useMemo(() => !!allEnabledAccounts.find(
        a => a.account.platform.code === 'THINK_COPY_MT4' || a.account.platform.code === 'THINK_COPY_MT5'
    ), [allEnabledAccounts])
    // Live-only ThinkCopy ownership. hasThinkCopy above also counts demo accounts, which would let a
    // demo-only owner reach the ThinkCopy page and see an empty live-account table. Gate visibility on this.
    const hasLiveThinkCopy = useMemo(() => !!enabledLiveAccounts.find(
        a => a.account.platform.code === 'THINK_COPY_MT4' || a.account.platform.code === 'THINK_COPY_MT5'
    ), [enabledLiveAccounts])
    const hasPAMMManager = useMemo(() => !!liveAccounts.find(a => a.platformAccountType === 'Money_Manager'), [liveAccounts])
    const PAMMManagerLimitReached = useMemo(() =>
        liveAccounts.filter(a => a.platformAccountType === 'Money_Manager').length >= 1, [liveAccounts])
    const hasPAMMInvestor = useMemo(() => !!liveAccounts.find(a => a.platformAccountType === 'Investor'), [liveAccounts])
    const hasPAMM = useMemo(() => hasPAMMInvestor || hasPAMMManager, [hasPAMMInvestor, hasPAMMManager])
    const hasDemoContestAccount = useMemo(() => !!enabledDemoAccounts.find(a => a.platformAccountType === 'DemoContest'), [enabledDemoAccounts])
    const hasMini = useMemo(() => allEnabledAccounts.find(a => a.platformAccountType === 'Mini'), [allEnabledAccounts])
    const hasLiveMini = useMemo(() => liveAccounts.find(a => a.platformAccountType === 'Mini'), [liveAccounts])
    const hasLiveTT = useMemo(() => liveAccounts.find(a => a.platformAccountType === 'ThinkTrader'), [liveAccounts])
    const hasLiveJapanSpread = useMemo(() => liveAccounts.find(a => a.platformAccountType === 'JapanSpread'), [liveAccounts])
    const hasOnlyComissionsAcc = useMemo(() => liveAccounts.some(a => a.platformAccountType === 'Commission') && liveAccounts.length === 1, [liveAccounts])

    return {
        availabeForDeposit,
        availabeForTransfer,
        enabledLiveAccounts,
        enabledDemoAccounts,
        allEnabledAccounts,
        hasDemoAccounts,
        hasLiveAccounts,
        latestLiveAccount,
        isDemo,
        demoAccounts,
        liveAccounts,
        hasFundedLiveAccount,
        allLiveEnabledTTcfds,
        TTcfdsWithBalance,
        hasPAMMManager,
        hasPAMMInvestor,
        hasPAMM,
        PAMMManagerLimitReached,
        totalLiveAccounts,
        hasThinkCopy,
        hasLiveThinkCopy,
        hasDemoContestAccount,
        hasMini,
        hasLiveMini,
        hasOnlyComissionsAcc,
        hasLiveTT,
        hasLiveJapanSpread,
    }
}

const useSortedAccounts = (sort: AccountsSort = AccountsSort.Default) => {
    const { liveAccounts, demoAccounts } = useAppState()

    const accounts = useMemo(() => [...liveAccounts, ...demoAccounts], [demoAccounts, liveAccounts])

    const allCFDAccounts = useMemo(() => {
        return accounts.filter(a =>
            a.account.accountTradingType.accountTradingType === "CFD"
        )
    }, [accounts])

    const enabledCFDAccounts = useMemo(() => {
        return allCFDAccounts.filter(a => !a.archived)
    }, [allCFDAccounts])

    const archivedCFDAccounts = useMemo(() => {
        return allCFDAccounts.filter(a => a.archived)
    }, [allCFDAccounts])

    const enabledShareAccounts = useMemo(() => {
        return accounts.filter(a =>
            a.account.accountTradingType.accountTradingType !== "CFD" &&
            !a.archived
        )
    }, [accounts])

    const archivedShareAccounts = useMemo(() => {
        return accounts.filter(a =>
            a.account.accountTradingType.accountTradingType !== "CFD" &&
            a.archived
        )
    }, [accounts])

    const allEnabledAccounts = useMemo(() => {
        return accounts.filter(a => !a.archived)
    }, [accounts])

    const allEnabledLiveAccounts = useMemo(() => {
        return allEnabledAccounts.filter(a => a.account.type === TradeAccountType.Live)
    }, [allEnabledAccounts])

    const allActiveLiveAccounts = useMemo(() => {
        return accounts.filter(a => a.account.type === TradeAccountType.Live &&
            !a.archived)
    }, [accounts])

    const allActiveDemoAccounts = useMemo(() => {
        return accounts.filter(a => a.account.type === TradeAccountType.Demo &&
            !a.archived)
    }, [accounts])

    const allLiveCFDAccounts = useMemo(() => {
        return allCFDAccounts.filter(a => a.account.type === TradeAccountType.Live)
    }, [allCFDAccounts])

    const allArchivedAccounts = useMemo(() => {
        return accounts.filter(a => a.archived)
    }, [accounts])

    const accountTypeOrder = useMemo<Record<TradingAccountType, number>>(() => ({
        "Mini": 1,
        "Classic": 2,
        "Zero": 3,
        "standard": 4,
        "Cent": 5,
        "think_invest": 6,
        "ThinkZero": 7,
        "ThinkTrader": 8,
        "spreadBetting": 9,
        "Money_Manager": 10,
        "Investor": 10,
        "trade_interceptor": 12,
        "CFD_Copy": 13,
        "CFD_COPY_MT4": 13,
        "CFD_COPY_MT5": 13,
        "DemoContest": 14,
        "Commission": 15,
        "JapanSpread": 16,
    }), [])

    const platformOrder = useMemo<Record<TradingPlatformName, number>>(() => ({
        "ThinkTrader": 1,
        "ThinkCopy": 2,
        "THINK_COPY_MT4": 2,
        "THINK_COPY_MT5": 2,
        "MT4": 3,
        "MT5": 4,
        "PAMM_MT5": 5,
        "PAMM_TRADING": 5,
        "PAMM_Trading": 5,
        "ThinkInvest": 6,
        "TradeInterceptor": 7,
        "THINK_COPY": 8
    }), [])

    const defaultSort = useCallback((accounts: AccountStat[]) => {
        return accounts.sort((a, b) => {
            const { platformAccountType: accountA = 'standard',
                account: { accountTradingType: { accountTradingType: productA },
                    platform: { name: platformA } } } = a
            const { platformAccountType: accountB = 'standard',
                account: { accountTradingType: { accountTradingType: productB },
                    platform: { name: platformB } } } = b
            switch (true) {
                case productA !== productB:
                    return productA === "CFD" ? 1 : -1
                case platformA !== platformB:
                    return platformOrder[platformA] - platformOrder[platformB]
                case platformA === platformB:
                    return accountTypeOrder[accountA] - accountTypeOrder[accountB]
                default: return 0
            }
        })
    }, [accountTypeOrder, platformOrder])

    const platformReverse = useCallback((accounts: AccountStat[]) => {
        return accounts.sort((a, b) => {
            const { platformAccountType: accountA = 'standard',
                account: { accountTradingType: { accountTradingType: productA },
                    platform: { name: platformA } } } = a
            const { platformAccountType: accountB = 'standard',
                account: { accountTradingType: { accountTradingType: productB },
                    platform: { name: platformB } } } = b
            switch (true) {
                case productA !== productB:
                    return productA === "CFD" ? 1 : -1
                case platformA !== platformB:
                    return platformOrder[platformB] - platformOrder[platformA]
                case platformA === platformB:
                    return accountTypeOrder[accountA] - accountTypeOrder[accountB]
                default: return 0
            }
        })
    }, [accountTypeOrder, platformOrder])

    const currencySort = useCallback((accounts: AccountStat[]) => {
        return accounts.sort((a, b) => {
            const { account: { currency: { code: currencyA } } } = a
            const { account: { currency: { code: currencyB } } } = b
            return currencyA.localeCompare(currencyB)
        })
    }, [])

    const sortedAccounts = useMemo(() => {
        switch (sort) {
            case AccountsSort.Status:
                return [
                    ...defaultSort(allArchivedAccounts),
                    ...defaultSort(allActiveDemoAccounts),
                    ...defaultSort(allActiveLiveAccounts),
                ]
            case AccountsSort.Platform:
                return [
                    ...platformReverse(allActiveLiveAccounts),
                    ...platformReverse(allActiveDemoAccounts),
                    ...platformReverse(allArchivedAccounts)
                ]
            case AccountsSort.Currency:
                return currencySort(accounts)
            default: return [
                ...defaultSort(allActiveLiveAccounts),
                ...defaultSort(allActiveDemoAccounts),
                ...defaultSort(allArchivedAccounts)
            ]
        }
    }, [accounts, allActiveDemoAccounts, allActiveLiveAccounts, allArchivedAccounts, currencySort, defaultSort, platformReverse, sort])

    const sortedDemo = useMemo(() =>
        sortedAccounts.filter(a => a.account.type === 'DEMO')
        , [sortedAccounts])

    const sortedLive = useMemo(() =>
        sortedAccounts.filter(a => a.account.type === 'LIVE')
        , [sortedAccounts])

    const sortedActiveLive = useMemo(() => {
        return sortedAccounts.filter(a => a.account.type === TradeAccountType.Live &&
            !a.archived)
    }, [sortedAccounts])


    const sortedArchivedLive = useMemo(() => {
        return sortedAccounts.filter(a => a.account.type === TradeAccountType.Live &&
            a.archived)
    }, [sortedAccounts])

    const sortedActiveDemo = useMemo(() => {
        return sortedAccounts.filter(a => a.account.type === TradeAccountType.Demo &&
            !a.archived)
    }, [sortedAccounts])

    const sortedArchivedDemo = useMemo(() => {
        return sortedAccounts.filter(a => a.account.type === TradeAccountType.Demo &&
            a.archived)
    }, [sortedAccounts])

    const sortedArchived = useMemo(() => {
        return [...sortedArchivedLive, ...sortedArchivedDemo]
    }, [sortedArchivedDemo, sortedArchivedLive])

    return {
        enabledShareAccounts,
        enabledCFDAccounts,
        archivedCFDAccounts,
        archivedShareAccounts,
        allEnabledAccounts,
        allEnabledLiveAccounts,
        allLiveCFDAccounts,
        allArchivedAccounts,
        allCFDAccounts,
        allActiveLiveAccounts,
        allActiveDemoAccounts,
        sortedAccounts,
        sortedDemo,
        sortedLive,
        sortedActiveDemo,
        sortedArchivedDemo,
        sortedActiveLive,
        sortedArchivedLive,
        sortedArchived,
        accountTypeOrder
    }

}

const useEmailValidation = (t: TFunction) => {
    const validate = useCallback((email?: string) => {
        if (!email || !email.length)
            return t('common:email.errors.notDefined')
        if (!Utils.isValidEmail(email))
            return t('common:email.errors.notValid')
        if (email.length > EMAIL_MAX_LENGTH)
            return t('common:email.errors.tooLong', { max: EMAIL_MAX_LENGTH })
        return ''
    }, [t])

    return validate
}

// THPRT-6358: setFormLastField sends these values to the BE, which reads them via
// Validator.get(String) -> Gson getAsString(); a non-primitive throws
// UnsupportedOperationException and the progress write fails. Radio/select field
// values arrive here as objects (SelectOption {name,value}, Phone {code,number})
// through the `any`-typed extraActionOnChange, so coerce any value to a meaningful
// scalar string before it is stored and sent.
const toScalarString = (value?: unknown): string | undefined => {
    if (value === null || value === undefined) return undefined
    if (typeof value !== 'object') return String(value)
    const o = value as Record<string, unknown>
    const scalar = o.value ?? o.number ?? o.name
    if (typeof scalar === 'string' || typeof scalar === 'number' || typeof scalar === 'boolean') return String(scalar)
    return JSON.stringify(value)
}

const useSetLastFieldName = () => {
    const currentField = useRef<string | undefined>(undefined)
    const currentValue = useRef<string | undefined>(undefined)
    const dispatch = useDispatch()

    const setLastFieldName = useCallback((name: string, value?: unknown) => {
        const scalar = toScalarString(value)
        if (name !== currentField.current) {
            if (currentField.current && currentValue.current !== undefined) {
                dispatch(sendLastField({
                    formLastField: currentField.current,
                    formLastFieldCompleted: currentValue.current
                }))
            }
            currentField.current = name
            currentValue.current = scalar
        } else if (scalar !== undefined) {
            currentValue.current = scalar
        }
    }, [dispatch])

    const flushLastField = useCallback(() => {
        if (currentField.current && currentValue.current !== undefined) {
            dispatch(sendLastField({
                formLastField: currentField.current,
                formLastFieldCompleted: currentValue.current
            }))
            currentField.current = undefined
            currentValue.current = undefined
        }
    }, [dispatch])

    return { setLastFieldName, flushLastField }
}

const topLevelProfileRoutes = [
    routes.profile.personalDetails,
    routes.profile.uploadDocuments,
    routes.profile.systemSettings.root,
]

const useApplicationAccountType = () => {
    const state = useAppState()
    const { uncompletedApplication: app, userProfile, onboardingCountry: userCountry } = state
    const accountTradingType = Utils.retrieveAccTradingType(app?.accountTradingTypes)

    const isAUCFD = useMemo(() => {
        return Utils.checkIfCountryIsAustralia(userProfile?.country || userCountry) &&
            accountTradingType === AccountTradingTypes.ForexAndCFDs
    }, [accountTradingType, userCountry, userProfile?.country])

    const isAUShares = useMemo(() => {
        return Utils.checkIfCountryIsAustralia(userProfile?.country || userCountry) &&
            accountTradingType === AccountTradingTypes.CashEquitiesASX
    }, [accountTradingType, userCountry, userProfile?.country])

    const isFTSAShares = useMemo(() => {
        return accountTradingType === AccountTradingTypes.CashEquitiesJSE
    }, [accountTradingType])

    const isCFD = useMemo(() => {
        return accountTradingType === AccountTradingTypes.ForexAndCFDs
    }, [accountTradingType])

    const isShares = useMemo(() => {
        return accountTradingType === AccountTradingTypes.CashEquitiesASX
    }, [accountTradingType])
    return {
        isAUCFD, isAUShares, isFTSAShares, isCFD, isShares
    }
}

const useBackButton = () => {
    const { redirectedFromWTR } = useAppState()
    const history = useHistory()
    const dispatch = useDispatch()
    const location = useLocation()
    const { loggedIn, userInsidePortal } = useAppState()
    const wtrApp = Utils.wtrAppSession() || redirectedFromWTR
    const { prevStep } = useVisualSteps()

    const handleBackClick = useCallback(() => {
        const goBack = () => {
            if (wtrApp) Utils.postMessageToWTR({ message: 'tradePage' })
            else {
                if (userInsidePortal) {
                    history.push(routes.dashboard.accounts.root)
                } else {
                    history.push(routes.account.login)
                    if (loggedIn) dispatch(logout())
                }
            }
        }

        if (topLevelProfileRoutes.includes(location.pathname))
            history.push(routes.dashboard.accounts.root)
        else {
            const step = prevStep({})
            if (step) {
                if (loggedIn) {
                    if (step.step >= -1) history.push(step.url)
                    else goBack()
                }
                else
                    history.push(step.url) //create <-> personanal info
            }
            else goBack()
        }
    }, [location, history, wtrApp, userInsidePortal, loggedIn, dispatch, prevStep])

    return handleBackClick
}

const useRedirectToWTRhandler = (targetProp?: string) => {
    const target = targetProp || `${Math.random()}`
    const { userProfile, language } = useAppState()
    const dispatch = useDispatch()
    const history = useHistory()
    const { theme } = useThemes()
    const { firePlatform } = useDashboardAnalytics()

    const handleRedirectToWTR = useCallback(async (selectedAccount?: Account, targetMode?: 'LIVE' | 'DEMO', wtrAdditionalParams?: WtrAdditionalParams) => {
        try {
            // THPRT-6264: a session without auth-service tokens (a fresh
            // registerdemo sign-up) cannot mint an SSO token; the request would
            // be misread as an expired session and log the client out. Guarding
            // here covers every WTR entry point (Trade Now, account cards,
            // confirmation pages) rather than each caller separately.
            if (!api.VerifyTokens()) {
                history.push(routes.dashboard.downloads)
                return
            }
            const redirectUrl = `${appConfig.WTR_URL}/account/sso`
            const mode = selectedAccount ? selectedAccount.type : (targetMode || 'LIVE')
            const userEmail = wtrAdditionalParams?.email || userProfile?.email
            const response = await api.generateSsoToken()
            if (response.ssoCode) {
                const wtrLang = WTRLanguage[language]
                let url = `${redirectUrl}?ssoToken=${response.ssoCode}&email=${userEmail}&mode=${mode}&theme=${theme}&lang=${wtrLang}`
                if (wtrAdditionalParams) {
                    const { targetPage, status, transactionId, accountNumber } = wtrAdditionalParams
                    url += `&targetPage=${targetPage}&status=${status}&transactionId=${transactionId}&accountNumber=${accountNumber}`
                }
                window.open(url, target)
                firePlatform('launched', {
                    platform: selectedAccount?.platform?.name || 'ThinkTrader',
                    launchType: 'web',
                })
            }
        } catch (e: any) {
            dispatch(appError(Utils.customError(e.message)))
        }
    }, [userProfile, theme, language, target, dispatch, history, firePlatform])

    return handleRedirectToWTR
}

const useScreen = () => {
    const { screen } = useAppState()
    const notDesktop = useMemo(() => mobileDevice || screen !== ScreenSize.Desktop, [screen])
    const isDesktop = useMemo(() => !mobileDevice && screen === ScreenSize.Desktop, [screen])
    const isMobile = useMemo(() => mobileDevice || screen === ScreenSize.Mobile, [screen])
    const isTablet = useMemo(() => !mobileDevice && screen === ScreenSize.Tablet, [screen])
    return {
        notDesktop,
        isDesktop,
        isMobile,
        isTablet
    }
}

const useValidation = () => {
    const { t } = useTranslation('account')
    const { portalAccountDomain, phoneCountries } = useAppState()
    const isTMJP = portalAccountDomain === PortalAccountDomain.TMJP

    const required = useCallback((value: any) => {
        if (!value || (typeof value === 'string' && value.trim() === ''))
            return t('common:notDefined')
        return undefined
    }, [t])

    const makeRequiredValidator = useCallback((customError?: string) => {
        const error = customError || t('common:notDefined')
        return (value: any) => {
            if (!value || (typeof value === 'string' && value.trim() === ''))
                return error
            else return undefined
        }
    }, [t])

    const cardNumberLength = useCallback((value: string) => {
        return value && value.length === 4 ? undefined : t('common:cardNumberLength')
    }, [t])

    const validateKanji = useCallback((value?: string) => {
        if (value && !value.match(kanjiNameRegex)) {
            return t('common:kanjiOnly')
        }
    }, [t])

    const validateJapaneseSymbols = useCallback((value?: string) => {
        if (value && !value.match(allJapaneseSymbolsRegex)) {
            return t('common:japaneseSymbolsOnly')
        }
    }, [t])

    const validateKatakana = useCallback((value?: string) => {
        if (value && !value.match(katakanaNameRegex)) {
            return t('common:katakanaOnly')
        }
    }, [t])

    const minmaxChars = useCallback((value: any, options?: { min?: number, max?: number }) => {
        const { min, max } = options || {}
        if (min && max) {
            const testExpr = new RegExp(`^.{${min},${max}}$`)
            if (!testExpr.test(value)) return t('common:minmaxChars', { min: min, max: max })
        }
        else if (min) {
            const testExpr = new RegExp(`^.{${min},}$`)
            if (!testExpr.test(value)) return t('common:minChars', { min: min })
        }
        else if (max) {
            const testExpr = new RegExp(`^.{1,${max}}$`)
            if (!testExpr.test(value)) return t('common:maxChars', { max: max })
        }
        return undefined
    }, [t])


    const isAlphanumeric = useCallback((value: any, options?: { min?: number, max?: number }) => {
        if (!value) return t('common:notDefined')
        if (!/^[A-Za-z0-9]+$/.test(value)) return t('common:isAlphanumeric')
        return minmaxChars(value, options)
    }, [minmaxChars, t])

    const isNumeric = useCallback((value: any, options?: { min?: number, max?: number }) => {
        if (!value) return t('common:notDefined')
        if (!/^[0-9]+$/.test(value)) return t('common:isNumeric')
        if (options) {
            const { min, max } = options
            if (min && max) {
                const testExpr = new RegExp(`^[0-9]{${min},${max}}$`)
                if (!testExpr.test(value)) return t('common:minmaxChars', { min: min, max: max })
            }
            else if (min) {
                const testExpr = new RegExp(`^[0-9]{${min},}$`)
                if (!testExpr.test(value)) return t('common:minChars', { min: min })
            }
            else if (max) {
                const testExpr = new RegExp(`^[0-9]{1,${max}}$`)
                if (!testExpr.test(value)) return t('common:maxChars', { max: max })
            }
        }
        return undefined
    }, [t])

    const validateNumberOfDigits = useCallback((numOfDigits: number, value?: any, optional = false) => {
        if (!value && optional) return undefined
        else {
            if (!value) return t('common:notDefined')
            const testExpr = new RegExp(`^[0-9]{${numOfDigits}}$`)
            if (!testExpr.test(value)) return t('common:id.notValidLength', { numOfDigits: numOfDigits })
            return undefined
        }
    }, [t])

    const validateID = useCallback((value?: any) => {
        if (value && !/^\d$/.test(value!)) return t('common:id.notValid')
    }, [t])

    const validateAustralianTFX = useCallback((value?: string, optional = false) => {
        return validateNumberOfDigits(9, value, optional)
    }, [validateNumberOfDigits])

    const validateACN = useCallback((value?: string, optional = false) => {
        return validateNumberOfDigits(9, value, optional)
    }, [validateNumberOfDigits])

    const validateUKInsuranceNumber = useCallback((value?: string, optional = false) => {
        if (!value) return t('common:notDefined')
        if (Utils.isDevEnv()) return undefined
        return !/^[a-zA-Z]{2}(?:\s*\d\s*){6}[a-zA-Z]$/.test(value)
            ? t('common:invalidUKInsuranceNumber')
            : undefined
    }, [t])

    const validatePhone = useCallback((phone?: Phone) => {
        if (Utils.isDevEnv()) return undefined
        if (phone && phone.number) {
            const fullNumber = '+' + phone.code + phone.number
            const country = phoneCountries.find(c => c.phoneCode === phone.code)
            const countryCode = country?.code2 as CountryCode | undefined
            if (countryCode && isSupportedCountry(countryCode)) {
                const phoneNumber = parsePhoneNumberFromString(fullNumber, countryCode)
                if (!phoneNumber) return t('common:phone.invalid')
                if (!isValidPhoneNumber(fullNumber)) return t('common:phone.notExists')
            } else {
                const shortNumber = phone?.code === 376 //Andorra
                    || phone?.code === 687 //New Caledonia
                const maxDigits = shortNumber ? 6 : 7
                const phoneNumber = parsePhoneNumberFromString(fullNumber)
                if (!phoneNumber) return t('common:phone.invalid')
                if (phoneNumber.nationalNumber.length < maxDigits) return t('common:phone.short', { digits: maxDigits })
                if (!phoneNumber.isPossible()) return t('common:phone.notExists')
            }
        } else {
            return t('common:phone.pleaseEnter')
        }
    }, [phoneCountries, t])

    const passwordTesters = {
        matchUppercase: /^.*[A-Z]+/,
        matchLowercase: /^.*[a-z]+/,
        matchNumbers: /^.*[0-9]+/,
        matchSpecialSymbols: /^.*[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+/,
    }

    const chineseRegex = /[^\u4E00-\u9FFF\u3400-\u4DFF\uF900-\uFAFF]+/g

    const validateName = useCallback((value?: string, country?: Country) => {
        if (!value) return t('common:notDefined')
        if (nameSpecialSymbolsRegex.test(value)) return t('common:invalidChars')
        if (nameNumbersRegex.test(value)) return t('common:invalidChars')
        if (!country) return ''
        if (Utils.checkIfCountryIsChina(country) && value.match(chineseRegex)) {
            return t('account:firstName.chineseOnly')
        }
        return undefined
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t])

    const validateEmail = useCallback((email: string) => {
        if (!email)
            return t('common:email.errors.notDefined')
        if (!Utils.isValidEmail(email))
            return t('common:email.errors.notValid')
        if (email.length > EMAIL_MAX_LENGTH)
            return t('common:email.errors.tooLong', { max: EMAIL_MAX_LENGTH })
        return undefined
    }, [t])

    const validateWebsite = useCallback((value: string) => {
        if (!value)
            return t('common:website.errors.notDefined')
        if (!value.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g))
            return t('common:website.errors.notValid')
        return undefined
    }, [t])

    const validateDate = useCallback((date?: Date) => {
        if (!date) return t('common:notDefined')
        if (!moment(date).isValid())
            return t('common:invalidDate')
        return undefined
    }, [t])

    const validateDOB = useCallback((dob?: Date) => {
        const minimumAge = isTMJP ? 20 : 18
        const maximumAge = isTMJP ? 80 : 100
        const invalid = validateDate(dob)
        if (invalid) return invalid
        const date = moment(dob)
        const now = moment()
        const diff = now.diff(date, 'years')
        if (now.isBefore(date)) return t('common:dateInFuture')
        if (diff < minimumAge) return t('account:birthDate.under18')
        if (diff > maximumAge) return t('account:birthDate.moreThan100yo')
        return undefined
    }, [t, validateDate, isTMJP])

    const validateDateNotInFuture = useCallback((date?: Date) => {
        const invalid = validateDate(date)
        if (invalid) return invalid
        if (moment(date).isAfter(moment()))
            return t('common:dateInFuture')
        return undefined
    }, [t, validateDate])

    const validateOptionalName = useCallback((value: string) => {
        if (!value) return t('common:notDefined')
        if (nameSpecialSymbolsRegex.test(value)) return t('common:invalidChars')
        return undefined
    }, [t])

    const validateCountry = useCallback((value: Country) => {
        if (!value) return t('account:country.placeholder')
    }, [t])

    return {
        required,
        cardNumberLength,
        isAlphanumeric,
        isNumeric,
        validateNumberOfDigits,
        validateAustralianTFX,
        validateID,
        validatePhone,
        validateName,
        validateEmail,
        passwordTesters,
        chineseRegex,
        validateJapaneseSymbols,
        validateKanji,
        validateKatakana,
        validateDOB,
        validateDate,
        validateWebsite,
        validateOptionalName,
        validateDateNotInFuture,
        validateCountry,
        makeRequiredValidator,
        validateACN,
        minmaxChars,
        validateUKInsuranceNumber
    }
}

const useAppState = () => {
    return useSelector((state: Store) => state.App)
}

// EU onboarding treatment, precedence: origin → registered profile country → IP.
// The marketing origin (?src and /referrer, already collapsed into originSrc at
// detection) decides first — EU origin → EU, non-EU origin → not EU, regardless of
// the rest. With no origin, a logged-in client's REGISTERED profile country wins
// over transient IP geolocation; only a fresh visitor with no profile falls through
// to the IP. Keyed off the stable profile country (not the live dropdown selection)
// so picking a country never reshapes the list. Restricts the country list to EU/EEA.
const useIsEuRestricted = () => {
    const originSrc = useSelector((state: Store) => state.App.originSrc)
    const userIP = useSelector((state: Store) => state.App.userIP)
    const profileCountryCode3 = useSelector((state: Store) => state.App.userProfile?.country?.code3)
    if (originSrc === null && profileCountryCode3)
        return EU_EEA_COUNTRY_CODES.has(profileCountryCode3)
    return isEuRestricted(originSrc, userIP?.country_code)
}

///TODO remove???
export const usePAMMState = () => {
    return useSelector((state: Store) => state.PAMM)
}

const useQuestionsRef = (app: AppInfo | null): QuestionsIDs[] => {
    // const useQuestions = (app: AppInfo | null): QuestionsIDs[] => {
    const q = useRef(app && app.accountApplicationQuestionDetails
        ? app.accountApplicationQuestionDetails : [])
    return q.current
}

const useYesNoOptions = () => {
    const { t } = useTranslation()

    return useMemo(() =>
        [
            { name: t(`common:yes`), value: true },
            { name: t(`common:no`), value: false }
        ], [t])
}


const useAccountTypeString = () => {
    const { t } = useTranslation()
    const { portalAccountDomain } = useAppState()
    const isAU = portalAccountDomain === PortalAccountDomain.AU
    return useCallback((account?: AccountTradingTypeUnion, country?: Country | null) => {
        if (!account) return ''
        const contryString = isAU ? "account:accountTradingType.AUSNZL" : 'account:accountTradingType.ZAF'
        if (account === 'CE') {
            return t(`${contryString}.Shares`)
            // DEBUG
            // } else if (account === 'CFD-COPY') {
            //     return t(`${contryString}.cfdCopy`)
        } else {
            return t(`${contryString}.ForexAndCFDs`)
        }
    }, [isAU, t])
}

const useApplicationTypeString = () => {
    const typeString = useAccountTypeString()
    return useCallback((app?: UserApplication) => {
        return app
            ? typeString(app.accountTradingType.accountTradingType, app.country!)
            : null
    }, [typeString])
}


const useQuery = () => {
    return new URLSearchParams(useLocation().search)
}

type FormFields = AddressFields & {
    accountHolderFirstName?: FieldValidation
    accountHolderLastName?: FieldValidation
    accountHolderDayOfBirth?: FieldValidation
    accountHolderMonthOfBirth?: FieldValidation
    accountHolderYearOfBirth?: FieldValidation
    originCountry?: FieldValidation
    accountHolderTitle?: FieldValidation
    accountHolderMiddleName?: FieldValidation
    accountHolderMobilePhone?: FieldValidation
    accountHolderMobilePhoneCode?: FieldValidation
    accountHolderPhoneCode?: FieldValidation
    accountHolderPhone?: FieldValidation
    accountHolderIdNumber?: FieldValidation
}

type FormFieldsIndexed = FormFields & {
    [key: string]: FieldValidation
}

const useAppRules = (country_id?: number) => {
    const [appRules, setAppRules] = useState<FormFields>()
    const { uncompletedApplication } = useAppState()

    useEffect(() => {
        let mounted = true

        const getValidationRules = async (country_id: number) => {
            const response = await api.getAppRules(country_id, uncompletedApplication?.accountType)
            if (response.payload[0].status === 'OK') {
                const rules = response.payload[0].result
                if (mounted) {
                    const data: FormFieldsIndexed = {}
                    rules.fields.forEach(r => (data[r.fieldName] = { optional: r.optional }))
                    //always show phone
                    data.accountHolderPhone = { optional: false }
                    if (data.accountHolderMobilePhone) delete data.accountHolderMobilePhone
                    setAppRules(data)
                }
            }
        }

        if (country_id) {
            getValidationRules(country_id)
        }

        return () => { mounted = false }
    }, [country_id, uncompletedApplication?.accountType])

    return { appRules }
}

const useAccountVerifiedEvent = () => {
    const { accountsLoaded, userProfile } = useAppState()
    const { latestLiveAccount } = useAccounts()
    const checked = useRef(false)
    useEffect(() => {
        if (accountsLoaded && latestLiveAccount && !checked.current) {
            checked.current = true
            if (latestLiveAccount.tradeNumber === 0 && latestLiveAccount.balance === 0) {
                window.dataLayer?.push({
                    'event': 'Account Verified',
                    'user id': userProfile?.id
                })
            }
        }
    }, [accountsLoaded, latestLiveAccount, userProfile?.id])
}

// THPRT-6368: durable per-user profile flag persisting that kyc_approved was
// already sent. This is the once-ever-per-user gate: it is server-side, so it
// survives logout, a fresh login, and a different browser or device (unlike the
// localStorage guard below, which is only same-browser).
const KYC_APPROVED_SENT_ATTR = 'kycApprovedEventSent'

// Fast, same-browser guard: prevents a double fire across tabs or within the
// window before the durable profile flag has been persisted and re-read. Keyed on
// the user id; localStorage access is wrapped so private-mode or quota errors can
// never break app init. The localStorage latch and the durable profile flag are
// written only when fire() reports that the event was actually pushed, so a
// swallowed push can never mark the user as already-sent.
const fireKycApprovedOncePerBrowser = (userId: string | number, fire: () => boolean) => {
    const key = `tm_evt_kyc_approved_${userId}`
    let alreadyFired = false
    try { alreadyFired = !!localStorage.getItem(key) } catch { alreadyFired = false }
    if (alreadyFired) return
    if (!fire()) return
    try { localStorage.setItem(key, '1') } catch { /* noop */ }
    api.addAttributeToUserProfile({ [KYC_APPROVED_SENT_ATTR]: 'yes' }).catch(() => { /* best-effort */ })
}

const useKycApprovedEvent = () => {
    const { accountsLoaded, userProfile } = useAppState()
    const { hasApproved, approvedApp } = useUserApplications()
    const { enabled, fireKycApproved } = useDashboardAnalytics()

    useEffect(() => {
        if (!enabled || !accountsLoaded) return
        const userId = userProfile?.id
        if (userId == null) return
        if (!hasApproved) return
        // Durable, cross-device gate: skip if the profile already records it as sent.
        if (userProfile?.additionalAttributes?.[KYC_APPROVED_SENT_ATTR] === 'yes') return
        fireKycApprovedOncePerBrowser(userId, () =>
            fireKycApproved({
                org: approvedApp?.organization?.name || userProfile?.organization?.name,
                simplified: approvedApp?.isSimplifiedOnboarding,
                eventId: `kyc_approved_${userId}`,
            })
        )
    }, [enabled, accountsLoaded, userProfile, hasApproved, approvedApp, fireKycApproved])
}

function useClickOutside<T extends HTMLElement>(handler: () => void) {
    const ref = useRef<T>(null)

    useEffect(() => {
        const handleMouseDown = (event: MouseEvent) => {
            if (!ref.current || ref.current.contains(event.target as Node)) {
                return
            }
            handler()
        }
        document.addEventListener('mousedown', handleMouseDown)
        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
        }
    }, [handler])

    return { ref }
}

const useLanguageID = () => {
    const { preferredLanguages, language } = useSelector((state: Store) => state.App, shallowEqual)

    const getLanguageId = useCallback((country?: Country) => {
        let preferredLanguage = 1
        if (preferredLanguages) {
            let lang = preferredLanguages.find(l => {
                return country?.organization?.name === PortalAccountDomain.TMJP
                    ? l.language_code === 'ja'
                    : l.language_code === language
            })
            if (lang) preferredLanguage = lang.id
        }
        return preferredLanguage
    }, [language, preferredLanguages])

    return getLanguageId
}

const useDangerousHTML = () => {
    const p = useCallback((html: string, className?: string) =>
        <p className={className} dangerouslySetInnerHTML={{
            __html: html
        }} />, [])
    const d = useCallback((html: string, className?: string) =>
        <div className={className} dangerouslySetInnerHTML={{
            __html: html
        }} />, [])
    const s = useCallback((html: string, className?: string) =>
        <span className={className} dangerouslySetInnerHTML={{
            __html: html
        }} />, [])
    return { p, d, s }
}

const useInterval = (action: () => void, interval: number) => {
    useEffect(() => {
        const int = setInterval(action, interval)
        return () => { clearInterval(int) }
    }, [action, interval])
}

const useTimeout = () => {
    return useCallback((action: () => void, interval: number) => {
        const int = setTimeout(action, interval)
        return () => { clearTimeout(int) }
    }, [])
}

const useRenderLink = () => {
    return {
        renderLink: useCallback((url: string, text: string, className?: string) => {
            return renderToString(<HttpLink url={url} className={className}>
                {text}
            </HttpLink>)
        }, [])
    }
}

const useDynamicLeverage = () => {
    const { isTMBM, isTMLC, isTMSY, isTFSA } = useOrganization()
    return useCallback((
        platformName?: TradingPlatformName | TradingPlatformID,
        accountType?: TradingAccountType
    ) => {
        // The Cent (USC) account is a dynamic-leverage product wherever the
        // backend enables it, independent of org/country (THPRT-6103).
        if (accountType === 'Cent') return true
        // TT (THPRT-5621) and MT5 (THPRT-6289) accounts are dynamic for these
        // entities; TFBO returns only integer leverage, so the FE overrides
        // the displayed label.
        const isDynamicLeverageEntity = isTMBM || isTMLC || isTMSY || isTFSA
        switch (platformName) {
            case 'ThinkTrader':
            case 'MT5': return isDynamicLeverageEntity
            default: return false
        }
    }, [isTFSA, isTMBM, isTMLC, isTMSY])
}

const useDynamicLeverageLabel = () => {
    const { t } = useTranslation()
    const { isTMLC, isTMSY, isTFSA } = useOrganization()
    return (isTMLC || isTMSY || isTFSA)
        ? t('dashboard.account.dynamicLeverage5000')
        : t('dashboard.account.dynamicLeverage')
}

const useSelectPlatform = (flow: AccountCreationFlow, type: TradeAccountType) => {
    const { userProfile } = useAppState()
    const { platforms, multiTypeTT } = usePlatforms(flow, type)
    const getCurrencyDescription = useCurrencyName()

    const showPlatformDetails = useCallback((platformName?: TradingPlatformFullName) => {
        switch (platformName) {
            case 'THINK_COPY': return type === 'DEMO'
                ? false
                : flow === 'additional'
                    ? true
                    : false
            case 'ThinkTrader': return true
            default: return !!platformName
        }
    }, [flow, type])

    const showPlatformCurrency = useCallback((platformName?: TradingPlatformFullName,
        accountType?: TradingAccountType) => {
        return showPlatformDetails(platformName)// && !!accountType
    }, [showPlatformDetails])

    const showPlatformLeverage = useCallback((platformName?: TradingPlatformFullName,
        accountType?: TradingAccountType) => {
        return showPlatformCurrency(platformName, accountType)
    }, [showPlatformCurrency])

    const validCurrencyForPlatform = useCallback((
        platformName?: TradingPlatformFullName,
        currency?: string,
        accountType?: TradingAccountType) => {
        if (!currency || !platformName) return false
        const platform = platforms.find(p => p.name === platformName)
        const platformCurrencies = platform?.accounts.find(a => a.type === accountType)?.currencies || []
        return platformCurrencies.indexOf(currency) !== -1
    }, [platforms])


    const defaultCurrency = useCallback((platformName: TradingPlatformFullName,
        currency: string,
        accountType?: TradingAccountType) => {
        if (validCurrencyForPlatform(platformName, currency, accountType))
            return {
                name: getCurrencyDescription(currency),
                value: currency
            }
        else return undefined
    }, [validCurrencyForPlatform, getCurrencyDescription])

    const defaultLeverage = useCallback((accountType?: TradingAccountType) => {
        if (accountType === 'Mini') return 2000
        const organization = userProfile?.organization
        return Utils.leverageString2Number(organization?.defaultLeverage)
    }, [userProfile])

    const defaultLeverageOption = useCallback((accountType?: TradingAccountType) => {
        const l = defaultLeverage(accountType)
        return {
            name: `1:${l}`,
            value: l
        }
    }, [defaultLeverage])

    return {
        validCurrencyForPlatform,
        defaultLeverage,
        defaultLeverageOption,
        defaultCurrency,
        showPlatformDetails,
        showPlatformCurrency,
        showPlatformLeverage,
    }
}

const useOrganizationChanged = () => {
    const { userProfile, portalAccountDomain } = useAppState()
    const isSAatTMSY = Utils.checkIfCountryIsSA(userProfile?.country) &&
        portalAccountDomain === PortalAccountDomain.TMSY
    return { isSAatTMSY }
}

const usePixelTracking = () => {
    const { userProfile, onboardingCountry: userCountry, language } = useAppState()

    const isArabic = useMemo(() => {
        const countries = ["SAU", "ARE", "OMN", "CAT", "JOR", "BHR", "KWT", "EGY", "LBN",]
        const code = userProfile?.country?.code3 || userCountry?.code3 || ''
        return language === 'ar' ||
            countries.includes(code)
    }, [language, userCountry?.code3, userProfile?.country?.code3])

    const start = useMemo(() =>
        isArabic
            ? <img src="https://px.adentifi.com/Pixels?a_id=6917;uq=[CACHEBUSTER];"
                height="0" width="0" style={{ display: "none", border: 0 }} alt="start" />
            : null
        , [isArabic])

    const submit = useMemo(() =>
        isArabic
            ? <img src="https://px.adentifi.com/Pixels?a_id=6916;uq=[CACHEBUSTER];"
                height="0" width="0" style={{ display: "none", border: 0 }} alt="submit" />
            : null
        , [isArabic])

    return { start, submit }
}

const useQnAforJPNapplication = (questionID: number) => {
    const dispatch = useDispatch()
    const { uncompletedApplication: appInfo, questions } = useAppState()
    const qs = useQuestionsRef(appInfo)
    const questionObject = useMemo(() => questions?.find(x => x.id === questionID), [questionID, questions])

    useEffect(() => {
        if (!questions || questions.length === 0) dispatch(getQuestions())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getAccountAppQsDetails = useCallback((answerValue?: boolean) => {
        const cleanedQs = [...qs].filter(x => ![questionID].includes(x.question || 0))
        const answerObject = questionObject?.answers.find(x => x.label === (answerValue ? 'yes' : 'no'))
        const accountApplicationQuestionDetails = [...cleanedQs, { question: questionID, answer: (answerObject?.id || 0) }]
        return accountApplicationQuestionDetails
    }, [qs, questionID, questionObject])

    return { getAccountAppQsDetails }
}

const useSimplifyOnboardingCheck = () => {
    const { uncompletedApplication: app, userProfile,
        portalAccountDomain, onboardingDomain,
        appStatus } = useAppState()
    const { getIBInfo } = useIBOnboarding()

    const checkForSimplifiedOnboarding = useCallback((userCountry?: Country, accountType?: TradingAccountType) => {
        const country = userCountry || userProfile?.country
        const ib = app.afsAid !== undefined
            ? getIBInfo(app.afsAid, country?.code3 || '')
            : undefined
        if (ib && ib.flow === 'simplified') return true
        const accType = accountType || app.platformAccountType
        const organization = userProfile?.organization?.name || onboardingDomain || portalAccountDomain
        if (Utils.checkIfCountryIsUAE(country) || Utils.checkIfCountryIsSA(country)) {
            return organization === PortalAccountDomain.TMLC
                ? true : false
        }
        if (country?.isSimplifyOnboarding) {
            if (accType === 'Money_Manager') {
                return false
            }
            return true
        }

        return false
    }, [getIBInfo, onboardingDomain, portalAccountDomain, app, userProfile])

    const checkForExSimplifyOnboarding = useCallback((accountType?: TradingAccountType) => {
        return checkForSimplifiedOnboarding(userProfile?.country, accountType)
    }, [checkForSimplifiedOnboarding, userProfile?.country])

    const checkIfPendingReview = useMemo(() => {
        return appStatus === 'PENDING_REVIEW' || appStatus === 'PENDING_E_VERIFICATION'
    }, [appStatus])

    const checkIfPendingDocs = useMemo(() => {
        const pendingDocsStatuses: ApplicationStatus[] = ['MANUAL_DOCS', 'PENDING_KYC', 'PENDING_E_VERIFICATION_RETRY']
        return appStatus && pendingDocsStatuses.includes(appStatus)
    }, [appStatus])

    return {
        checkForExSimplifyOnboarding,
        checkIfPendingReview,
        checkIfPendingDocs,
        checkForSimplifiedOnboarding
    }
}

const useOrganization = () => {
    const { userProfile, portalAccountDomain, onboardingDomain, onboardingCountry: userCountry } = useAppState()

    const domain = useMemo(() => {
        if (userProfile) return userProfile.organization.name
        return onboardingDomain || portalAccountDomain || userCountry?.organization?.name
    }, [onboardingDomain, portalAccountDomain, userCountry, userProfile])

    const isAU = useMemo(() => { return domain === PortalAccountDomain.AU }, [domain])
    const isUK = useMemo(() => { return domain === PortalAccountDomain.UK }, [domain])
    const isTFSA = useMemo(() => { return domain === PortalAccountDomain.TFSA }, [domain])
    const isTMBM = useMemo(() => { return domain === PortalAccountDomain.TMBM }, [domain])
    const isTMLC = useMemo(() => { return domain === PortalAccountDomain.TMLC }, [domain])
    const isTMSY = useMemo(() => { return domain === PortalAccountDomain.TMSY }, [domain])
    const isTMCY = useMemo(() => { return domain === PortalAccountDomain.TMCY }, [domain])
    const isTMEU = useMemo(() => { return domain === PortalAccountDomain.TMEU }, [domain])
    const isTMJP = useMemo(() => { return domain === PortalAccountDomain.TMJP }, [domain])
    const isTMNZ = useMemo(() => { return domain === PortalAccountDomain.TMNZ }, [domain])

    return {
        isAU,
        isUK,
        isTFSA,
        isTMBM,
        isTMLC,
        isTMSY,
        isTMCY,
        isTMEU,
        isTMJP,
        isTMNZ,
    }
}

// THPRT-6264: gates the multi demo account activation flow. TMJP keeps the
// legacy single-account demo flow regardless of the config flag.
const useMultiDemoFlow = () => {
    const [flagOn, setFlagOn] = useState(false)
    const { isTMJP } = useOrganization()
    useEffect(() => {
        appInit.then(config => setFlagOn(!!config.MULTI_DEMO_ACCOUNTS_ENABLED))
            .catch(() => setFlagOn(false))
    }, [])
    return flagOn && !isTMJP
}

const useOnboardingCountryList = (isTMJP: boolean, ibInfo: IBCookie | null) => {
    const { countries, phoneCountries, userProfile } = useAppState()
    const euRestricted = useIsEuRestricted()
    const introducedBy = userProfile?.additionalAttributes?.introducedBy
    return useMemo(() => {
        const pid = introducedBy ?? ibInfo?.pid
        const whitelist = getIBCountryWhitelist(pid)
        const extras = phoneCountries.filter(c =>
            whitelist.includes(c.code3) && !countries.some(x => x.code3 === c.code3)
        )
        const all = extras.length
            ? [...countries, ...extras].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
            : countries
        if (euRestricted) return filterToEuEea(all)
        return isTMJP ? all : all.filter(x => x.code3 !== 'JPN')
    }, [countries, phoneCountries, introducedBy, ibInfo, isTMJP, euRestricted])
}

const usePlatformAvailability = (flow: AccountCreationFlow) => {
    const { hasThinkCopy: hasTCApplication, simplifiedAppNotApprovedYet } = useUserApplications()
    const { isTMBM, isTMSY, isTMLC } = useOrganization()
    const { hasThinkCopy } = useAccounts()
    const { livePlatforms } = useAppState()

    const isThinkCopyPlatformEnabled = useMemo(() => {
        return flow === 'additional' &&
            (isTMBM || isTMSY || isTMLC) &&
            livePlatforms.some(p => p.name === 'THINK_COPY')
    }, [flow, isTMBM, isTMLC, isTMSY, livePlatforms])

    const isPAMMPlatformEnabled = useMemo(() => {
        return livePlatforms.some(p => p.name === 'PAMM_TRADING')
    }, [livePlatforms])

    const isThinkCopyBannerEnabled = useMemo(() => {
        if (simplifiedAppNotApprovedYet) return false
        return isThinkCopyPlatformEnabled && !(hasThinkCopy || hasTCApplication)
    }, [hasTCApplication, hasThinkCopy, isThinkCopyPlatformEnabled, simplifiedAppNotApprovedYet])

    return {
        isThinkCopyPlatformEnabled,
        isThinkCopyBannerEnabled,
        isPAMMPlatformEnabled
    }
}

const usePlatforms = (flow: AccountCreationFlow, demoLive: TradeAccountType, ttOnly?: boolean) => {
    const { demoPlatforms, livePlatforms, onboardingCountry: userCountry, portalAccountDomain } = useAppState()
    const isLive = demoLive === TradeAccountType.Live
    const { isUK, isTMLC } = useOrganization()
    const isFrance = userCountry?.code3 === 'FRA'
    const { isThinkCopyPlatformEnabled } = usePlatformAvailability(flow)
    const { isCapitalIndex } = useIntroducer()

    const sortOrder: Record<TradingPlatformName, number> = useMemo(() => {
        // Specific order for Vietnam, THPRT-6135
        if (userCountry?.code3 === 'VNM') {
            return {
                "MT5": 1,
                "MT4": 2,
                "ThinkTrader": 3,
                "THINK_COPY": 4,
                "THINK_COPY_MT4": 4,
                "THINK_COPY_MT5": 4,
                "PAMM_Trading": 5,
                "PAMM_MT5": 5,
                "PAMM_TRADING": 6,
                "ThinkInvest": 7,
                "TradeInterceptor": 8,
            }
        }

        if (isCapitalIndex()) {
            return {
                "MT4": 1,
                "ThinkTrader": 2,
                "MT5": 3,
                "THINK_COPY": 4,
                "THINK_COPY_MT4": 4,
                "THINK_COPY_MT5": 4,
                "PAMM_Trading": 5,
                "PAMM_MT5": 5,
                "PAMM_TRADING": 6,
                "ThinkInvest": 7,
                "TradeInterceptor": 8,
            }
        }

        return {
            "ThinkTrader": 1,
            "MT5": 2,
            "MT4": 3,
            "THINK_COPY": 4,
            "THINK_COPY_MT4": 4,
            "THINK_COPY_MT5": 4,
            "PAMM_Trading": 5,
            "PAMM_MT5": 5,
            "PAMM_TRADING": 6,
            "ThinkInvest": 6,
            "TradeInterceptor": 7,
        }
    }, [isCapitalIndex, userCountry])

    const allowedPlatforms = useMemo(() => {
        let platforms = isLive
            ? [...livePlatforms]
            : demoPlatforms.filter(p => p.name.toUpperCase() !== 'PAMM_TRADING' &&
                p.name !== 'THINK_COPY')
        if (!isThinkCopyPlatformEnabled)
            platforms = platforms.filter(p => p.name !== 'THINK_COPY')
        if (flow === 'onboarding')
            platforms = platforms.filter(p => p.name.toUpperCase() !== 'PAMM_TRADING')
        platforms.sort((a, b) => {
            return sortOrder[a.name] - sortOrder[b.name]
        })
        return platforms
    }, [demoPlatforms, flow, isLive, isThinkCopyPlatformEnabled, livePlatforms, sortOrder])

    const TT = useMemo(() => {
        return allowedPlatforms.find(p => p.name === 'ThinkTrader')
    }, [allowedPlatforms])

    const multiTypeTT = useMemo(() => {
        return TT && TT.accounts.length > 1
    }, [TT])

    const platforms = useMemo(() => {
        if (isCapitalIndex() && flow === 'onboarding') {
            const mt4Only = allowedPlatforms.filter(p => p.name === 'MT4')
            return mt4Only.length ? mt4Only : allowedPlatforms
        }
        if (ttOnly)
            return allowedPlatforms.filter(p => p.name === 'ThinkTrader')
        // THPRT-6412: the UK org, including DIFC (UAE), no longer offers MT5. DIFC was
        // previously MT5/ThinkTrader and is now ThinkTrader only (MT4 was never offered here).
        if (Utils.checkDIFCentity(userCountry, portalAccountDomain)) {
            return allowedPlatforms.filter(p => p.name === 'ThinkTrader')
        }

        let result = allowedPlatforms
        //THPRT-6279 (AU/TFSA), THPRT-6412 (UK, incl. DIFC handled above): no MT5 on any account creation.
        if (portalAccountDomain === PortalAccountDomain.AU || portalAccountDomain === PortalAccountDomain.TFSA || isUK)
            result = result.filter(p => p.name !== 'MT5')
        //THPRT-5803
        if (flow === 'onboarding' && (isUK || (isFrance && !isTMLC)))
            result = result.filter(p => p.name !== 'MT4')

        return result
    }, [ttOnly, allowedPlatforms, userCountry, portalAccountDomain, flow, demoLive, isUK, isFrance, isTMLC, isCapitalIndex])

    return { platforms, TT, multiTypeTT }
}

const useDebounce = (callback: Function) => {
    const ref = useRef<Function>()

    useEffect(() => {
        ref.current = callback
    }, [callback])

    const debouncedCallback = useMemo(() => {
        const func = () => {
            ref.current?.()
        }

        return debounce(func, 500)
    }, [])

    return debouncedCallback
}

const useRemainingDeposit = (stats?: AccountStat) => {
    const [deposit, setDeposit] = useState<RemainingDeposit>({
        netSumOfAppliedDeposits: 0,
        netMinDepositAmount: 0,
        currencyCode: ""
    })
    const [checked, setChecked] = useState(false)

    const shouldCheck = useMemo(() => {
        return stats
            ? stats.account.type === TradeAccountType.Live &&
            stats.platformAccountType === 'Mini'
            : false
    }, [stats])

    useEffect(() => {
        if (stats) {
            const checkDeposit = async () => {
                try {
                    const response = await api.getRemainigDeposit(stats.account.id)
                    api.checkTFBOResponse(response)
                    setDeposit(response.payload[0].result)
                    setChecked(true)
                } catch { }
            }
            if (shouldCheck) checkDeposit()
            else setChecked(true)
        }
    }, [shouldCheck, stats])

    const deposited = useMemo(() => {
        return shouldCheck && checked
            ? deposit.netSumOfAppliedDeposits >= deposit.netMinDepositAmount
            : true
    }, [checked, deposit, shouldCheck])

    const remainingSum = useMemo(() => {
        return checked
            ? `${(deposit.netMinDepositAmount - deposit.netSumOfAppliedDeposits).toFixed(2)} ${deposit.currencyCode}`
            : null
    }, [deposit, checked])

    return {
        remainigDeposit: deposit,
        shouldCheck,
        deposited,
        remainingSum
    }
}

const useHideThinkZeroForVietnamMarkupIb = (): boolean => {
    const dispatch = useDispatch()
    const ibInfo = useSelector((state: Store) => state.App.ibInfo)
    const { introducedBy } = useUserApplications()
    const afsAid = ibInfo?.afsAid
    const resolved = ibInfo?.isVietnamMarkupIb
    const partnerSource = afsAid || introducedBy

    useEffect(() => {
        if (!partnerSource || resolved !== undefined) return
        const partnerId = Number(partnerSource)
        if (!Number.isFinite(partnerId)) return
        let active = true
        executeApi<boolean>(() => api.isVietnamMarkupIb(partnerId)).then(({ ok, result }) => {
            if (!active) return
            if (ok && typeof result === 'boolean')
                dispatch(setIB({ ...ibInfo, isVietnamMarkupIb: result }))
        })
        return () => { active = false }
    }, [partnerSource, resolved, ibInfo, dispatch])

    return !!partnerSource && resolved !== false
}

const usePlatformAccountTypeOptions = (
    platform: TradingPlatform | undefined,
    country?: Country | undefined,
    additionalAccount: boolean = false) => {
    const { t } = useTranslation()
    const { accountTypeOrder } = useSortedAccounts()
    const { isCapitalIndex } = useIntroducer()
    const hideThinkZero = useHideThinkZeroForVietnamMarkupIb()

    return useMemo<SelectOption<TradingAccountType>[]>(() => {
        if (!platform) return []
        let accounts = platform.accounts
        const pamm = platform.name.toUpperCase() === "PAMM_TRADING"
        if (platform.name === 'MT4' && isCapitalIndex() && !additionalAccount)
            accounts = accounts.filter(a => a.type === 'standard')
        const types = accounts.map(
            a => {
                const { type } = a
                let description = t(`accounts:${platform.name}.${type}`)
                if (pamm && type === "Money_Manager")
                    description = t(`accounts:${platform.name}.PAMM_Master`)
                if (pamm && type === "Investor")
                    description = t(`accounts:${platform.name}.PAMM_Investor`)
                return {
                    name: t(`common:accountType.values.${type}`, type),
                    description,
                    value: type,
                }
            }
        )
        let result = types
        const noMini = (platform?.name === 'MT4') || (country && NON_MINI_TMBM_COUNTRIES.includes(country.code3))
        if (noMini)
            result = result.filter(t => !(t.value === 'Mini'))
        if (hideThinkZero)
            result = result.filter(t => t.value !== 'ThinkZero')

        result.sort((a, b) => accountTypeOrder[a.value] - accountTypeOrder[b.value])
        result.filter(t => t.value !== 'think_invest')
        return result
    }, [accountTypeOrder, country, isCapitalIndex, platform, t, hideThinkZero, additionalAccount])
}

const useEmbeddedLink = ({
    text,
    onClick,
    className,
    linkClassName = 'link'
}: {
    text: string
    onClick: () => void
    className?: string
    linkClassName?: string
}
) => {
    const click = useCallback((event) => {
        if (event.target.nodeName === 'SPAN') {
            onClick()
        }
    }, [onClick])

    return useMemo(() => {
        const mask = new RegExp('\{%(.*?)%\}', 'g')
        const matches = mask.exec(text)
        const __html = text.replace(mask, _ => {
            return matches && matches.length > 1
                ? `<span class="${linkClassName}">${matches[1]}</span>`
                : text
        })
        return <div onClick={click} className={className} dangerouslySetInnerHTML={{ __html }} />
    }, [className, click, linkClassName, text])
}

const useLandingPage = () => {
    const { landingPage } = useAppState()
    const { application: ibApplication } = useIBState()
    const sidebarItems = useMenuEntries()
    const url = useMemo(() => {
        const isCorrectPath = (path?: string) => {
            if (path && path.startsWith(ibPath) && !ibApplication) return false
            const allowed = {
                ...Utils.deepFlattenToObject(routes.dashboard, 'd'),
                ...Utils.deepFlattenToObject(routes.ib, 'i'),
                ...Utils.deepFlattenToObject(routes.profile, 'p')
            }
            return Object.values(allowed).some(v => v === path)
        }
        const isCorrectLanding = (path?: string) => {
            let result = true
            const check = (items: DashboardMenuItem[]) => {
                items.forEach(i => {
                    if (!result) return
                    if (i.children) check(i.children)
                    if (i.link === path &&
                        ((i.shouldDisplay && i.shouldDisplay() === false) ||
                            i.disabled)) {
                        result = false
                    }
                })
            }
            check(sidebarItems)
            return result
        }
        if (landingPage &&
            isCorrectPath(landingPage.path)
            && isCorrectLanding(landingPage.path)) {
            return landingPage.path
        }
        return routes.dashboard.accounts.root
    }, [ibApplication, landingPage, sidebarItems])
    return url
}

const useShowTradingView = () => {
    const { userProfile } = useAppState()
    const { isDesktop } = useScreen()
    const { isTMJP } = useOrganization()
    const isTW = userProfile?.country?.code3 === "TWN"
    const isHK = userProfile?.country?.code3 === "HKG"
    return isDesktop && !(isTW || isHK || isTMJP)
}

const useShowThinkCapital = () => {
    // const { isTMLC, isTMBM } = useOrganization()
    // const { userProfile } = useAppState()
    // const code = userProfile?.country?.code3 || ""
    // return (isTMLC || isTMBM) && !["EGY", "VNM"].includes(code)
    //THPRT-5922
    return false
}

const useCreateLiveTT = () => {
    const history = useHistory()
    const { hasNoApplication } = useUserApplications()
    const dispatch = useDispatch()

    return useCallback(() => {
        if (hasNoApplication) {
            dispatch(setUserAppInfo({
                accountType: 'individual'
            }))
            history.push(routes.account.createAccount.live)
        }
        else history.push({
            pathname: routes.dashboard.accounts.createLive,
            state: {
                platform: 'ThinkTrader',
                accountType: 'ThinkTrader'
            }
        })
    }, [dispatch, hasNoApplication, history])
}

const useIntroducer = () => {
    const { userProfile } = useAppState()
    const { introducedBy } = useUserApplications()
    const { isAU } = useOrganization()

    const introducer = useMemo(() => {
        return userProfile?.additionalAttributes?.introducedBy ||
            localStorage.getItem(__STORAGE.introducedBy) ||
            introducedBy
    }, [introducedBy, userProfile])

    // Pure pid check (no org gate); see Utils.isCapitalIndexPid.
    const isCapitalIndexPid = useCallback((pid?: string) => Utils.isCapitalIndexPid(pid), [])

    // Capital Index treatment applies to the AU organisation only.
    const isCapitalIndex = useCallback((ib?: string) => {
        if (!isAU) return false
        return isCapitalIndexPid(ib || introducer)
    }, [introducer, isAU, isCapitalIndexPid])

    // Record the Capital Index introducer on the pure pid, not isCapitalIndex: this
    // runs at mount, before the applicant's country/org is chosen, so isAU is not yet
    // resolved. Gating it on isAU here would drop the marker for a genuine AU applicant
    // and the treatment would never turn on. isCapitalIndex() still applies the isAU
    // gate at render, and the durable attribution paths (afsAid, flushIntroducedBy) are
    // gated by the selected org, so a non-AU applicant is still not attributed.
    const checkOnboardingIntroducer = useCallback(() => {
        const ib = Utils.getIBcookie()
        if (ib && isCapitalIndexPid(ib.pid)) {
            localStorage.setItem(__STORAGE.introducedBy, ib.pid)
            return isCapitalIndex(ib.pid)
        }
        return false
    }, [isCapitalIndexPid, isCapitalIndex])

    return { introducer, checkOnboardingIntroducer, isCapitalIndex, isCapitalIndexPid }
}
const useCheckOngoingAppStatus = () => {
    const dispatch = useDispatch()
    return useCallback(async () => {
        const response = await api.checkApplicationStatuses()
        const { application_status } = response.payload[0].result[0]
        if (application_status !== 'INCOMPLETE') {
            dispatch(setAppStatus(application_status as ApplicationStatus))
            dispatch(getUserApps())
        }
    }, [dispatch])
}
const useThinkRewardsAvailable = () => {
    const { loyaltyStatus, userProfile } = useAppState()
    return isThinkRewardsAvailable(loyaltyStatus, userProfile)
}

const useIsLoyaltyProgramScreen = () => {
    const { pathname } = useLocation()
    return pathname === routes.dashboard.thinkRewards
}

const useApplicationSubmit = () => {
    const dispatch = useDispatch()
    const { executeCaptcha } = useReCaptcha()

    return useCallback(async (app: Partial<AppInfo>) => {
        const token = await executeCaptcha()
        app.recaptchaResponse = token
        const response = await api.incrementalSubmit(app)
        const applicationStatus = response.payload[0].result.applicationStatus
        if (!app.completed) {
            setTimeout(() => {
                if (applicationStatus !== 'INCOMPLETE') {
                    dispatch(setAppStatus(applicationStatus))
                    dispatch(getUserApps())
                }
            }, 1000)
        }
        return response
    }, [dispatch, executeCaptcha])
}

export {
    useSimplifyOnboardingCheck,
    usePrevious,
    useShortTranslation,
    useScrollToTop,
    usePageTitle,
    useCurrencyFormatter,
    useFormatAmount,
    UseLinks,
    useIsGeneralSidebarEnabled,
    usePlatformName,
    use2FACheck,
    useCompleteAppLaterButton,
    useGetAccountTypesOptions,
    useUserApplications,
    useAccounts,
    useEmailValidation,
    useSetLastFieldName,
    useBackButton,
    useSortedAccounts,
    useValidation,
    useAppState,
    useIsEuRestricted,
    useQuestionsRef,
    useApplicationTypeString,
    useAccountTypeString,
    useQuery,
    useAppRules,
    useYesNoOptions,
    useAccountVerifiedEvent,
    useKycApprovedEvent,
    useThousandsFormatter,
    useClickOutside,
    useDateTimeFormatter,
    useLanguageID,
    useApplicationAccountType,
    useRedirectToWTRhandler,
    useScreen,
    useDangerousHTML,
    useInterval,
    useTimeout,
    useRenderLink,
    useSelectPlatform,
    useOrganizationChanged,
    usePixelTracking,
    useQnAforJPNapplication,
    useOrganization,
    useMultiDemoFlow,
    useOnboardingCountryList,
    usePlatformAvailability,
    useDebounce,
    usePlatforms,
    useRemainingDeposit,
    usePlatformAccountTypeOptions,
    useEmbeddedLink,
    useLandingPage,
    useShowTradingView,
    useShowThinkCapital,
    useCreateLiveTT,
    useIntroducer,
    useCheckOngoingAppStatus,
    useIsLoyaltyProgramScreen,
    useThinkRewardsAvailable,
    useApplicationSubmit,
    useDynamicLeverage,
    useDynamicLeverageLabel
}

